import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Loader,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import {
  SliderComponent,
  SodCustomDonutChart,
  SodCustomGradientLineChart,
} from '../../../../../../shared/components';
import type { UnitConsumptionData } from '../../../../types';

import OVERVIEW_CONTENT_CONSTANTS from './constants';
import { type OverviewContentProps } from './type';

import './OverviewContent.css';

/** Keys to skip when dynamically building legends from the response */
const EXCLUDED_KEYS = new Set(['totalUnitEntitlement', 'defaultMode']);

/**
 * Converts camelCase key to a human-readable label.
 * e.g. "unitConsumed" -> "Unit Consumed"
 * e.g. "digitalAccessDocCount" -> "Digital Access Doc Count"
 */
function camelToLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Generates legend items dynamically from the raw API response.
 * Derives id, label, and color purely from the response keys — no hardcoded map.
 */
function buildLegends(data: UnitConsumptionData) {
  return Object.entries(data)
    .filter(([key, val]) => {
      if (EXCLUDED_KEYS.has(key)) {
        return false;
      }

      // Only include entries that look like a metric object with "value"
      return val != null && typeof val === 'object' && 'value' in val;
    })
    .map(([key, val]) => {
      const item = val as {
        value: number | null;
        percentage?: number;
        utilization?: number;
        remaining?: number;
      };

      if (item.value == null) {
        return null;
      }

      return {
        id: key,
        label: camelToLabel(key),
        value: item.value.toLocaleString(),
        percentage: `${
          item.utilization ?? item.remaining ?? item.percentage ?? 0
        }%`,
      };
    })
    .filter(
      (
        item
      ): item is {
        id: string;
        label: string;
        value: string;
        percentage: string;
      } => item != null
    );
}

/**
 * Derives progress bar color from utilization vs threshold.
 * Green when below threshold, amber when approaching limit, red when over 90%.
 */
function getProgressColor(utilization: number, threshold: number): string {
  if (utilization <= threshold) {
    return 'var(--color-foreground-success-medium, #4caf50)';
  }

  if (utilization <= 90) {
    return 'var(--color-foreground-warning-medium, #f5a623)';
  }

  return 'var(--color-foreground-danger-medium, #e5362f)';
}

/**
 * @description Renders license metrics and analytics including FUE consumption,
 * license inventory, consumption trends (limited to latest 4 data points), and
 * identity type distribution with interactive charts and sliders. All data is
 * received via props from the parent Details component. Legend items and colors are
 * derived dynamically from the API response keys with no hardcoded mapping. Y-axis
 * range scales from the minimum data point value to the maximum data point value.
 * @param {OverviewContentProps} props - Component props (all data passed from parent)
 * @returns {React.ReactElement} Overview content grid with metric cards, sliders, and charts
 */
function OverviewContent({
  fueConsumptionData,
  isFueLoading,
  isFueFetching,
  isFueError,
  refetchFue,
  licenseInventoryData,
  isInventoryLoading,
  isInventoryFetching,
  isInventoryError,
  refetchInventory,
  trendData,
  isTrendLoading,
  isTrendFetching,
  isTrendError,
  refetchTrend,
  identityDistributionData,
  isDistributionLoading,
  isDistributionFetching,
  isDistributionError,
  refetchDistribution,
}: OverviewContentProps): React.ReactElement {
  // Toggle between Cost and FUE views for license inventory
  const [inventoryViewMode, setInventoryViewMode] = useState(1);

  // Dynamically build legends from raw API response
  const fueConsumptionLegends = useMemo(
    () => (fueConsumptionData ? buildLegends(fueConsumptionData) : []),
    [fueConsumptionData]
  );

  // Sort tiers by unitWeight descending (highest weight first)
  const sortedTiers = useMemo(() => {
    if (!licenseInventoryData) {
      return [];
    }

    return [...licenseInventoryData.tiers].sort(
      (a, b) => b.utilization - a.utilization
    );
  }, [licenseInventoryData]);

  const tiersToShow = sortedTiers;

  // Helpers to check if a card should show the empty placeholder
  const showFueEmpty =
    !fueConsumptionData && !isFueLoading && !isFueFetching && !isFueError;
  const showInventoryEmpty =
    !licenseInventoryData &&
    !isInventoryLoading &&
    !isInventoryFetching &&
    !isInventoryError;
  const showTrendEmpty =
    !trendData && !isTrendLoading && !isTrendFetching && !isTrendError;
  const showDistributionEmpty =
    !identityDistributionData &&
    !isDistributionLoading &&
    !isDistributionFetching &&
    !isDistributionError;

  return (
    <div className='LicenseManagement-OverviewContent'>
      <div className='LicenseManagement-OverviewContent-grid'>
        {(isFueLoading || (isFueFetching && Boolean(!fueConsumptionData))) && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--fueConsumption LicenseManagement-OverviewContent-card--loading'>
            <Loader kind='loop' format='main' color='inverse' />
          </Box>
        )}
        {isFueError && Boolean(!isFueFetching) && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--fueConsumption LicenseManagement-OverviewContent-card--error'>
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {OVERVIEW_CONTENT_CONSTANTS.FUE_LOAD_ERROR}
            </Typography>
            <Button kind='ghost' size='small' onClick={refetchFue}>
              {OVERVIEW_CONTENT_CONSTANTS.RETRY}
            </Button>
          </Box>
        )}
        {showFueEmpty && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--fueConsumption LicenseManagement-OverviewContent-card--empty'>
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {OVERVIEW_CONTENT_CONSTANTS.NO_LICENSE_DATA_TITLE}
            </Typography>
          </Box>
        )}
        {fueConsumptionData && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--fueConsumption'>
            <div className='LicenseManagement-OverviewContent-cardHeader'>
              <h3 className='LicenseManagement-OverviewContent-cardTitle'>
                {OVERVIEW_CONTENT_CONSTANTS.FUE_CONSUMPTION_TITLE}
              </h3>
              <span className='LicenseManagement-OverviewContent-percentage'>
                {fueConsumptionData.unitConsumed.utilization ?? 0}
                {OVERVIEW_CONTENT_CONSTANTS.PERCENT}
              </span>
            </div>
            <div className='LicenseManagement-OverviewContent-fueStats'>
              <div className='LicenseManagement-OverviewContent-fueValue'>
                <p className='LicenseManagement-OverviewContent-fueNumber'>
                  {fueConsumptionData.unitConsumed.value.toLocaleString() ?? 0}
                </p>
                <p className='LicenseManagement-OverviewContent-fueTotal'>
                  {OVERVIEW_CONTENT_CONSTANTS.SLASH}{' '}
                  {fueConsumptionData.totalUnitEntitlement.value.toLocaleString() ??
                    0}{' '}
                  {OVERVIEW_CONTENT_CONSTANTS.FUE}
                </p>
              </div>
              <p className='LicenseManagement-OverviewContent-fueDescription'>
                {OVERVIEW_CONTENT_CONSTANTS.FUE_DESCRIPTION}
              </p>
            </div>

            {/* Slider Component */}
            <div className='LicenseManagement-OverviewContent-sliderWrapper'>
              <SliderComponent
                value={fueConsumptionData.unitConsumed.utilization ?? 0}
                readOnly
                darkMode
                progressColor='var(--color-foreground-warning-medium, #FFC156)'
                trackHeight='0.375rem'
                animated
                animationDuration={2000}
              />
            </div>

            <div className='LicenseManagement-OverviewContent-legends'>
              {fueConsumptionLegends.map((legend) => (
                <div
                  key={legend.id}
                  className='LicenseManagement-OverviewContent-legend'>
                  <div className='LicenseManagement-OverviewContent-legendColor' />
                  <div className='LicenseManagement-OverviewContent-legendContent'>
                    <p className='LicenseManagement-OverviewContent-legendValue'>
                      {legend.value}
                    </p>
                    <p className='LicenseManagement-OverviewContent-legendLabel'>
                      {legend.label}
                    </p>
                  </div>
                  <p className='LicenseManagement-OverviewContent-legendPercentage'>
                    {legend.percentage}
                  </p>
                </div>
              ))}
            </div>
          </Box>
        )}
        {/* License Inventory Card - Using Slider */}
        {(isInventoryLoading ||
          (isInventoryFetching && Boolean(!licenseInventoryData))) && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--licenseInventory LicenseManagement-OverviewContent-card--loading'>
            <Loader kind='loop' format='main' color='inverse' />
          </Box>
        )}
        {isInventoryError && Boolean(!isInventoryFetching) && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--licenseInventory LicenseManagement-OverviewContent-card--error'>
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {OVERVIEW_CONTENT_CONSTANTS.INVENTORY_LOAD_ERROR}
            </Typography>
            <Button kind='ghost' size='small' onClick={refetchInventory}>
              {OVERVIEW_CONTENT_CONSTANTS.RETRY}
            </Button>
          </Box>
        )}
        {showInventoryEmpty && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--licenseInventory LicenseManagement-OverviewContent-card--empty'>
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {OVERVIEW_CONTENT_CONSTANTS.NO_LICENSE_DATA_TITLE}
            </Typography>
          </Box>
        )}
        {licenseInventoryData && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--licenseInventory'>
            <div className='LicenseManagement-OverviewContent-cardHeader LicenseManagement-OverviewContent-cardHeader--compact'>
              <div className='LicenseManagement-OverviewContent-cardHeaderRow'>
                <Typography kind='h3' color='--color-foreground-neutral-bold'>
                  {OVERVIEW_CONTENT_CONSTANTS.LICENSE_INVENTORY_TITLE}
                </Typography>
                <div className='LicenseManagement-OverviewContent-inventoryToggleGroup'>
                  <ToggleButtonGroup
                    selection={inventoryViewMode}
                    setSelection={setInventoryViewMode}
                    className='LicenseManagement-OverviewContent-inventoryToggle'>
                    <ToggleButton label='Cost' />
                    <ToggleButton label='FUE' />
                  </ToggleButtonGroup>
                </div>
              </div>
            </div>

            <div className='LicenseManagement-OverviewContent-inventorySummaryRow'>
              <Typography
                kind='body3'
                color='--color-foreground-neutral-medium'
                className='LicenseManagement-OverviewContent-inventorySummary'>
                {licenseInventoryData.summary}
              </Typography>
              <Typography
                kind='h4'
                color='--color-foreground-neutral-bold'
                className='LicenseManagement-OverviewContent-inventoryTotal'>
                {inventoryViewMode === 1
                  ? licenseInventoryData.totalAnnualCost.displayValue
                  : Number(
                      sortedTiers
                        .reduce((sum, t) => sum + t.consumedUnit, 0)
                        .toFixed(2)
                    ).toLocaleString()}
              </Typography>
            </div>

            {/* License Tiers with Sliders */}
            <div className='LicenseManagement-OverviewContent-tiers LicenseManagement-OverviewContent-tiers--compact'>
              {tiersToShow.map((tier) => (
                <div
                  key={tier.tier.toLowerCase().replace(/\s+/g, '-')}
                  className='LicenseManagement-OverviewContent-tier LicenseManagement-OverviewContent-tier--compact'>
                  <div className='LicenseManagement-OverviewContent-tierInfo'>
                    <p className='LicenseManagement-OverviewContent-tierName'>
                      {tier.tier} {OVERVIEW_CONTENT_CONSTANTS.DASH}{' '}
                      {tier.utilization}
                      {OVERVIEW_CONTENT_CONSTANTS.PERCENT}
                    </p>
                    <Tooltip
                      text={`${tier.tier}: ${Number(
                        tier.consumedUnit.toFixed(2)
                      ).toLocaleString()} of ${tier.contractedUnit.toLocaleString()} FUE consumed (${
                        tier.utilization
                      }%). ${
                        tier.utilization > tier.threshold
                          ? `Exceeds threshold of ${tier.threshold}%.`
                          : 'Within threshold.'
                      } ${tier.userCount.toLocaleString()} identities assigned.`}
                      placement='top'
                      className='LicenseManagement-OverviewContent-tierTooltip'
                      trigger={
                        <div className='LicenseManagement-OverviewContent-tierSliderWrapper'>
                          <SliderComponent
                            value={tier.utilization}
                            readOnly
                            darkMode
                            progressColor={getProgressColor(
                              tier.utilization,
                              tier.threshold
                            )}
                            trackHeight='0.375rem'
                            animated
                            animationDuration={2000}
                          />
                        </div>
                      }
                    />
                  </div>

                  <p className='LicenseManagement-OverviewContent-tierCost'>
                    {inventoryViewMode === 1
                      ? tier.displayCost
                      : Number(tier.consumedUnit.toFixed(2)).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </Box>
        )}
        {/* FUE Consumption Trend Card */}
        {(isTrendLoading || (isTrendFetching && Boolean(!trendData))) && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--trend LicenseManagement-OverviewContent-card--loading'>
            <Loader kind='loop' format='main' color='inverse' />
          </Box>
        )}
        {isTrendError && Boolean(!isTrendFetching) && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--trend LicenseManagement-OverviewContent-card--error'>
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {OVERVIEW_CONTENT_CONSTANTS.TREND_LOAD_ERROR}
            </Typography>
            <Button kind='ghost' size='small' onClick={refetchTrend}>
              {OVERVIEW_CONTENT_CONSTANTS.RETRY}
            </Button>
          </Box>
        )}
        {showTrendEmpty && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--trend LicenseManagement-OverviewContent-card--empty'>
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {OVERVIEW_CONTENT_CONSTANTS.NO_LICENSE_DATA_TITLE}
            </Typography>
          </Box>
        )}
        {trendData && trendData.dataPoints.length > 0 && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--trend'>
            <div className='LicenseManagement-OverviewContent-cardHeader'>
              <h3 className='LicenseManagement-OverviewContent-cardTitle'>
                {OVERVIEW_CONTENT_CONSTANTS.FUE_CONSUMPTION_TREND_TITLE}
              </h3>
            </div>

            <div className='LicenseManagement-OverviewContent-trendChart'>
              <SodCustomGradientLineChart
                data={(() => {
                  // Use only first 4 data points (latest simulation run)
                  const points = trendData.dataPoints.slice(0, 4);
                  const values = points.map((p) => p.value);
                  const minVal = Math.min(...values);
                  const maxVal = Math.max(...values);
                  const range = maxVal - minVal || 1;

                  return points.map((p, idx) => ({
                    x: (idx / (points.length - 1)) * 100,
                    y: ((p.value - minVal) / range) * 100,
                  }));
                })()}
                width='100%'
                height={220}
                viewBox='-12 -2 120 115'
                lineGradient={[
                  { offset: '0%', color: '#0294F7' },
                  { offset: '100%', color: '#01AE66' },
                ]}
                fillGradient={[
                  { offset: '0%', color: '#0294F7', opacity: 0.2 },
                  { offset: '100%', color: '#01AE66', opacity: 0 },
                ]}
                animate
                animationDuration={1.5}
                showFill
                darkMode
                yAxis={(() => {
                  const points = trendData.dataPoints.slice(0, 4);
                  const pointValues = points.map((p) => p.value);
                  const minVal = Math.min(...pointValues);
                  const maxVal = Math.max(...pointValues);
                  const step = (maxVal - minVal) / 4;

                  return {
                    labels: Array.from({ length: 5 }, (_, i) => ({
                      label: Math.round(minVal + step * i).toLocaleString(),
                      value: i * 25,
                    })),
                    showGridLines: true,
                    gridLineColor: '#2a3444',
                    fontSize: 5.5,
                  };
                })()}
                xAxis={{
                  labels: trendData.dataPoints
                    .slice(0, 4)
                    .map((p, idx, arr) => ({
                      label: p.month,
                      value: (idx / (arr.length - 1)) * 80 + 10,
                    })),
                  showGridLines: false,
                  fontSize: 5.5,
                }}
              />
            </div>
          </Box>
        )}

        {/* Identity Type Distribution Card */}
        {(isDistributionLoading ||
          (isDistributionFetching && Boolean(!identityDistributionData))) && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--distribution LicenseManagement-OverviewContent-card--loading'>
            <Loader kind='loop' format='main' color='inverse' />
          </Box>
        )}
        {isDistributionError && Boolean(!isDistributionFetching) && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--distribution LicenseManagement-OverviewContent-card--error'>
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {OVERVIEW_CONTENT_CONSTANTS.DISTRIBUTION_LOAD_ERROR}
            </Typography>
            <Button kind='ghost' size='small' onClick={refetchDistribution}>
              {OVERVIEW_CONTENT_CONSTANTS.RETRY}
            </Button>
          </Box>
        )}
        {showDistributionEmpty && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--distribution LicenseManagement-OverviewContent-card--empty'>
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {OVERVIEW_CONTENT_CONSTANTS.NO_LICENSE_DATA_TITLE}
            </Typography>
          </Box>
        )}
        {identityDistributionData && (
          <Box className='LicenseManagement-OverviewContent-card LicenseManagement-OverviewContent-card--distribution'>
            <div className='LicenseManagement-OverviewContent-cardHeader'>
              <h3 className='LicenseManagement-OverviewContent-cardTitle'>
                {OVERVIEW_CONTENT_CONSTANTS.IDENTITY_TYPE_DISTRIBUTION}
              </h3>
            </div>

            <div className='LicenseManagement-OverviewContent-distributionContent'>
              <SodCustomDonutChart
                data={identityDistributionData.segments.map((seg) => ({
                  category: seg.type,
                  value: seg.percentage,
                }))}
                colorRange={[...OVERVIEW_CONTENT_CONSTANTS.DISTRIBUTION_COLORS]}
                value={identityDistributionData.totalUnit}
                label='Total'
                width={141}
                height={141}
                innerRadius={50}
                darkMode
                animate
                animationDuration={1000}
                easing='easeOutQuad'
                hoverConfig={{
                  enableHalo: true,
                  haloWidth: 8,
                  haloOpacity: 0.3,
                }}
              />

              <div className='LicenseManagement-OverviewContent-distributionLegends'>
                {identityDistributionData.segments.map((seg, index) => (
                  <div
                    key={seg.type}
                    className='LicenseManagement-OverviewContent-distributionLegend'>
                    <div
                      className='LicenseManagement-OverviewContent-distributionColor'
                      style={{
                        backgroundColor:
                          OVERVIEW_CONTENT_CONSTANTS.DISTRIBUTION_COLORS[
                            index %
                              OVERVIEW_CONTENT_CONSTANTS.DISTRIBUTION_COLORS
                                .length
                          ],
                      }}
                    />
                    <p className='LicenseManagement-OverviewContent-distributionLabel'>
                      {seg.type} {OVERVIEW_CONTENT_CONSTANTS.DASH}{' '}
                      {seg.percentage}
                      {OVERVIEW_CONTENT_CONSTANTS.PERCENT}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Box>
        )}
      </div>
    </div>
  );
}

export default OverviewContent;
